/**
 * Card — content container with sensible defaults from the design tokens.
 *
 * <Card>
 *   <Card.Header>…</Card.Header>
 *   <Card.Body>…</Card.Body>
 *   <Card.Footer>…</Card.Footer>
 * </Card>
 *
 * Or just `<Card padding="md">content</Card>` for the common case.
 */
import React from 'react';
import { css } from './tokens';

export type CardPadding = 'none' | 'sm' | 'md' | 'lg';
export type CardElevation = 0 | 1 | 2 | 3;

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: CardPadding;
  elevation?: CardElevation;
  interactive?: boolean;
  /** Render with a 1px solid border instead of an elevation shadow. */
  bordered?: boolean;
  /** Optional accent stripe on the left edge (uses brand primary or accent color). */
  accent?: 'primary' | 'accent' | 'food' | 'rides' | 'warning' | null;
  as?: React.ElementType;
}

const PADDING_MAP: Record<CardPadding, string | number> = {
  none: 0,
  sm: 12,
  md: 16,
  lg: 24,
};

const ELEVATION_MAP: Record<CardElevation, string> = {
  0: 'none',
  1: css.shadow.sm,
  2: css.shadow.card,
  3: css.shadow.md,
};

const ACCENT_COLOR_MAP: Record<NonNullable<CardProps['accent']>, string> = {
  primary: css.color.primary,
  accent:  css.color.accent,
  food:    css.color.food,
  rides:   css.color.rides,
  warning: css.color.warning,
};

export const Card: React.FC<CardProps> & {
  Header: React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Body:   React.FC<React.HTMLAttributes<HTMLDivElement>>;
  Footer: React.FC<React.HTMLAttributes<HTMLDivElement>>;
} = ({
  padding = 'md',
  elevation = 2,
  interactive = false,
  bordered = false,
  accent = null,
  as: Tag = 'div',
  style,
  className,
  children,
  ...rest
}) => {
  const baseStyle: React.CSSProperties = {
    background: css.color.surface,
    color: css.color.text,
    borderRadius: css.radius.lg,
    border: bordered ? `1px solid ${css.color.border}` : `1px solid ${css.color.border}`,
    boxShadow: bordered ? 'none' : ELEVATION_MAP[elevation],
    padding: PADDING_MAP[padding],
    position: 'relative',
    overflow: 'hidden',
    cursor: interactive ? 'pointer' : undefined,
    transition:
      'box-shadow 200ms cubic-bezier(0.2,0,0,1), transform 200ms cubic-bezier(0.2,0,0,1), border-color 200ms ease',
    ...style,
  };

  const hoverStyle: React.CSSProperties | undefined = interactive
    ? {}
    : undefined;
  void hoverStyle; // not used directly; native :hover via inline event handler below

  return (
    <Tag
      style={baseStyle}
      className={['pm-card', interactive ? 'pm-card--interactive' : '', className]
        .filter(Boolean)
        .join(' ')}
      onMouseEnter={
        interactive
          ? (e: React.MouseEvent<HTMLDivElement>) => {
              e.currentTarget.style.boxShadow = css.shadow.cardHover;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }
          : undefined
      }
      onMouseLeave={
        interactive
          ? (e: React.MouseEvent<HTMLDivElement>) => {
              e.currentTarget.style.boxShadow = ELEVATION_MAP[elevation];
              e.currentTarget.style.transform = 'translateY(0)';
            }
          : undefined
      }
      {...rest}
    >
      {accent && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            background: ACCENT_COLOR_MAP[accent],
          }}
        />
      )}
      {children}
    </Tag>
  );
};

Card.Header = ({ style, className, children, ...rest }) => (
  <div
    style={{
      padding: '12px 16px',
      borderBottom: `1px solid ${css.color.border}`,
      background: css.color.surface,
      fontWeight: 600,
      ...style,
    }}
    className={['pm-card__header', className].filter(Boolean).join(' ')}
    {...rest}
  >
    {children}
  </div>
);
Card.Header.displayName = 'Card.Header';

Card.Body = ({ style, className, children, ...rest }) => (
  <div
    style={{ padding: 16, ...style }}
    className={['pm-card__body', className].filter(Boolean).join(' ')}
    {...rest}
  >
    {children}
  </div>
);
Card.Body.displayName = 'Card.Body';

Card.Footer = ({ style, className, children, ...rest }) => (
  <div
    style={{
      padding: '12px 16px',
      borderTop: `1px solid ${css.color.border}`,
      background: css.color.surface,
      ...style,
    }}
    className={['pm-card__footer', className].filter(Boolean).join(' ')}
    {...rest}
  >
    {children}
  </div>
);
Card.Footer.displayName = 'Card.Footer';

export default Card;

